/**
 * perf-overlay.js — the `?perf=1` readout.
 *
 * A corner panel showing frame rate, what the renderer actually submitted, and
 * how many physics bodies are live. It exists to answer "is this a render cost
 * or a physics cost?" at a glance while walking the midway, which is the one
 * question a headless test cannot answer for you.
 *
 * Sampled on a timer rather than every frame: reading `renderer.info` is free,
 * but writing six DOM nodes at 72 Hz is not, and a counter that updates faster
 * than you can read it is worse than one that does not.
 *
 * @module ui/perf-overlay
 */

const SAMPLE_MS = 250;

class PerfOverlay {
  constructor() {
    /** @type {HTMLElement} */ this.el = null;
    /** @type {Record<string, HTMLElement>} */ this.rows = {};
    this.engine = null;
    this.physics = null;
    this._last = 0;
    this._worstFps = Infinity;
    this.enabled = false;
  }

  /**
   * @param {{ engine: object, physics: object }} refs
   * @returns {PerfOverlay}
   */
  install({ engine, physics }) {
    this.engine = engine;
    this.physics = physics;

    this.el = document.getElementById('cc-perf');
    if (!this.el) return this;

    const labels = ['fps', 'worst', 'calls', 'tris', 'geom', 'bodies'];
    this.el.innerHTML = labels
      .map((k) => `<div class="r"><span>${k}</span><b data-k="${k}">—</b></div>`)
      .join('');
    for (const k of labels) {
      this.rows[k] = this.el.querySelector(`b[data-k="${k}"]`);
    }

    this.el.classList.add('on');
    this.enabled = true;
    return this;
  }

  /**
   * @param {number} dt Seconds, unused — the overlay paces itself on wall clock
   *   so it stays readable regardless of frame rate.
   */
  update(dt) {
    if (!this.enabled) return;
    const now = performance.now();
    if (now - this._last < SAMPLE_MS) return;
    this._last = now;

    const fps = Math.round(this.engine?.fps || 0);
    // Ignore the boot frames: the first render pays for shader compilation and
    // would pin "worst" to single digits for the whole session. 30 frames, not
    // 120 — under SwiftShader 120 frames take long enough that the row still
    // read as an em-dash minutes in, which looks like a broken overlay.
    if ((this.engine?.frameCount || 0) > 30 && fps > 0) {
      this._worstFps = Math.min(this._worstFps, fps);
    }

    const info = this.engine?.renderer?.info;
    this._set('fps', fps);
    this._set('worst', Number.isFinite(this._worstFps) ? this._worstFps : '—');
    this._set('calls', info ? info.render.calls : '—');
    this._set('tris', info ? fmt(info.render.triangles) : '—');
    this._set('geom', info ? info.memory.geometries : '—');
    this._set('bodies', this.physics?.bodyCount ? this.physics.bodyCount() : '—');
  }

  /** @returns {object} Current numbers, for `window.__cc`. */
  sample() {
    const info = this.engine?.renderer?.info;
    return {
      enabled: this.enabled,
      fps: Math.round(this.engine?.fps || 0),
      worstFps: Number.isFinite(this._worstFps) ? this._worstFps : null,
      calls: info ? info.render.calls : null,
      triangles: info ? info.render.triangles : null,
      geometries: info ? info.memory.geometries : null,
      textures: info ? info.memory.textures : null,
      bodies: this.physics?.bodyCount ? this.physics.bodyCount() : null,
    };
  }

  /** @private */
  _set(key, value) {
    const el = this.rows[key];
    if (el) el.textContent = String(value);
  }
}

/** @param {number} n @returns {string} 128400 → '128k'. */
function fmt(n) {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}k`;
  return String(n);
}

/** The one overlay. */
export const perfOverlay = new PerfOverlay();
